import { toActiveVehicleUsage } from "../utils/mappers/to-active-vehicle-usage";
import { toActiveVehicleUsageListItem } from "../utils/mappers/to-active-vehicle-usage-list-item";
import { toOverdueVehicleUsage } from "../utils/mappers/to-overdue-vehicle-usage";
import type {
	ActiveVehicleUsage,
	ActiveVehicleUsageListItem,
	OverdueVehicleUsage,
	VehicleUsageHistoryFilters,
} from "../types/vehicle-usage.types";
import {
	type FleetVehicleTelematicsInteractionDto,
	type VehicleUsageDto,
	endPrivateVehicleUsage,
	getActiveUsageForVehicle,
	getActiveVehicleUsages,
	getOverdueVehicleUsages,
	getVehicleUsagesForVehicle,
} from "@/lib/api";
import { toEndOfDayUtc, toStartOfDayUtc } from "@/utils/date.utilities";

export interface EndPrivateUsageParams {
	vehicleId: string;
	skipLock?: boolean;
	skipKeyFobCheck?: boolean;
}

export const vehicleUsageService = {
	async endPrivateUsage({
		vehicleId,
		skipLock = false,
		skipKeyFobCheck = false,
	}: EndPrivateUsageParams): Promise<VehicleUsageDto> {
		const telematicsInteraction: FleetVehicleTelematicsInteractionDto = skipLock
			? "SKIP"
			: "REQUIRED";

		// skipKeyFobCheck is ignored by the backend when telematics is fully
		// skipped, so only send it when telematics still runs.
		return endPrivateVehicleUsage({
			vehicleId,
			telematicsInteraction,
			...(skipKeyFobCheck ? { skipKeyFobCheck: true } : {}),
		});
	},

	async getActiveVehicleUsage(
		vehicleId: string
	): Promise<ActiveVehicleUsage | null> {
		const response = await getActiveUsageForVehicle(vehicleId);
		return toActiveVehicleUsage(response);
	},

	async getActiveVehicleUsages(): Promise<Array<ActiveVehicleUsageListItem>> {
		const response = await getActiveVehicleUsages();
		return response.items.map(toActiveVehicleUsageListItem);
	},

	async getVehicleUsagesForVehiclePaginated(
		vehicleId: string,
		filters: VehicleUsageHistoryFilters | undefined,
		page: number,
		size: number
	): Promise<{ items: Array<VehicleUsageDto>; totalElements: number }> {
		const response = await getVehicleUsagesForVehicle(vehicleId, {
			from: filters?.period?.start
				? toStartOfDayUtc(filters.period.start)
				: undefined,
			to: filters?.period?.end ? toEndOfDayUtc(filters.period.end) : undefined,
			bookingTypes: filters?.bookingTypes,
			userRemoteId: filters?.userRemoteId,
			page,
			size,
		});

		return {
			items: response.items,
			totalElements: response.totalElements,
		};
	},

	async getOverdueVehicleUsages(): Promise<Array<OverdueVehicleUsage>> {
		const response = await getOverdueVehicleUsages();
		return response.map(toOverdueVehicleUsage);
	},
};
